import React, { useEffect, useState } from 'react';
import '../../atoms/Card/Card.css';
import { Box, Button, Paper } from '@mui/material';
import ICON from '../../assets/SuccessList.svg'; // Replace with a dynamic icon if needed
import { useNavigate } from 'react-router-dom';
import { CommonSevice } from '../../services/common';
import { getAccessToken, getCRMAccessToken } from '../../config/msal-config';
import { useSelector } from 'react-redux';
import axios from 'axios';
import MeetingList from './teams/Meeting';
import * as momentTimezone from 'moment-timezone';
import CRMStatsDashboard from './teams/dynamicCRM';
import { getLocalStorageItem, setLocalStorage } from '../../utils/common.util';

export default function WidgetCard({ widget, isGroupWithFilter }) {
  const navigate = useNavigate();
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { currentUserRoleId, appPermissions, userInfo } = useSelector(
    (state) => state.main
  );
  const rawMSSO = localStorage.getItem('MSSO');
  const MSSO = rawMSSO ? JSON.parse(rawMSSO) : null;
  const [email, setEmail] = useState();
  const [accessToken, setAccessToken] = useState();
  const [crmaccessToken, setCRMAccessToken] = useState();
  const [summaryData, setSummaryData] = useState();
  const [extData, setExtData] = useState({});
  const {
    widget_title,
    widget_name,
    widget_desc,
    widget_summary_section_data,
    action_button1_caption,
    action_button1_method,
    action_button2_caption,
    action_button2_method,
    widget_on_click_method,
    widget_summary_data_source_view,
    widget_icon,
    action_1_permission,
    action_2_permission,
    view_data,
  } = widget || {};

  useEffect(async () => {
    if (view_data && !isGroupWithFilter) {
      setSummaryData(view_data);
    } else if (isGroupWithFilter && widget_summary_data_source_view) {
      const view = await new CommonSevice().getWidgetView(
        widget_summary_data_source_view,
        currentUserRoleId
      );
      setSummaryData(view);
    }
  }, [widget_summary_data_source_view, currentUserRoleId, widget_name]);

  useEffect(() => {
    const fetchConfig = async () => {
      const view = await new CommonSevice().getTenantDetails();
      const orgDetails = view?.reduce((acc, item) => {
        const { varName, varValue } = item;
        if (varName.trim() === 'EXT_APP__ENTRA_TENANT_ID')
          acc.tenantId = varValue;
        if (varName.trim() === 'EXT_APP__ENTRA_CLIENT_ID')
          acc.clientId = varValue;
        return acc;
      }, {});
      setLocalStorage('config', orgDetails);
    };

    const config = getLocalStorageItem('config');
    if (!config || !config.clientId || !config.tenantId) {
      fetchConfig();
    }
  }, []);

  useEffect(() => {
    setEmail(MSSO?.account?.username || userInfo?.companyEmail);
  }, [MSSO]);

  useEffect(() => {
    setAccessToken(MSSO?.accessToken);
  }, [MSSO]);

  useEffect(async () => {
    if (widget_name === 'ext_app_dynamics_crm') {
      const scope = await new CommonSevice().getTenantDetailsByConfigId(
        'EXT_APP__DYNAMICS_365_SCOPE'
      );
      await getCRMAccessToken(scope).then((res) => {
        setCRMAccessToken(res);
      });
    }
  }, [widget_name]);

  useEffect(() => {
    const loadTeamsAndChannels = async () => {
      const scope = await new CommonSevice().getTenantDetailsByConfigId(
        'EXT_APP__MICROSOFT_TEAMS_SCOPE'
      );

      const token = await getAccessToken(scope);
      try {
        // Step 1: Get joined teams
        const teamsRes = await fetch(
          'https://graph.microsoft.com/v1.0/me/joinedTeams',
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );
        const teamsData = await teamsRes.json();

        // Step 2: For each team, fetch channels
        const teamsWithChannelsData = await Promise.all(
          teamsData.value.map(async (team) => {
            const channelsRes = await fetch(
              `https://graph.microsoft.com/v1.0/teams/${team.id}/channels`,
              {
                headers: {
                  Authorization: `Bearer ${accessToken}`,
                },
              }
            );
            const channelsData = await channelsRes.json();
            return {
              id: team.id,
              displayName: team.displayName,
              channels: channelsData.value || [],
            };
          })
        );

        setExtData((prev) => ({
          ...prev,
          teams: teamsWithChannelsData,
        }));
      } catch (error) {
        console.error('Failed to fetch Teams/Channels', error);
      }
    };

    if (widget_name === 'ext_app_teams') {
      loadTeamsAndChannels();
    }
  }, [widget_name, accessToken]);

  useEffect(async () => {
    if (widget_name === 'ext_app_jira') {
      const scope = await new CommonSevice().getTenantDetailsByConfigId(
        'EXT_APP__MICROSOFT_TEAMS_SCOPE'
      );

      const token = await getAccessToken(scope);
      try {
        const issue = await new CommonSevice().getJiraView(email);

        setExtData((prev) => ({
          ...prev,
          jira: issue,
        }));
      } catch (error) {
        console.error('Failed to fetch Teams/Channels', error);
      }
    }
  }, [widget_name, email]);

  const handleAction = (method) => {
    const defaultRouteMatch = typeof method === 'string' && method.trim().startsWith('navigate');
  
    if (defaultRouteMatch) {
      const route = method.trim().replace('navigate(', '').replace(')', '').replace(/['"`]/g, '').trim();
  
      // Try to find a rec_id from summaryData
      const recId =
        Array.isArray(summaryData) &&
        summaryData.length > 0 &&
        summaryData[0]?.rec_id;

      if (recId) {
        navigate(`${route}/${recId}`);
      } else {
        navigate(route);
      }
    } else {
      window.open(method, '_blank');
    }
  };

  const summaryEntriesList = (summaryData || []).map((item) => {
    const grouped = Object.entries(item || {}).reduce((acc, [key, value]) => {
      const match = key.match(/^(?:date|data)(\d+)_(label|value|unit)$/);
      if (match && value != null) {
        const [_, index, type] = match;
        acc[index] = acc[index] || {};
        acc[index][type] = value;
      }
      return acc;
    }, {});

    // Filter valid entries with both label and value
    return Object.values(grouped).filter((entry) => entry.label && entry.value);
  });

  const handleOpenMore = (
    event,
    teamId,
    channelId,
    channelName,
    tenantId = 'e6fdbcc7-dcec-409f-8d1c-0eec6fa68f44'
  ) => {
    event.stopPropagation();

    const encodedChannelName = encodeURIComponent(channelName);
    const url = `https://teams.microsoft.com/l/channel/${channelId}/${encodedChannelName}?groupId=${teamId}&tenantId=${tenantId}&app=web`;

    window.open(url, '_blank');
  };

  const handleOpenMoreTeams = (
    event,
    remainingTeams,
    tenantId = 'e6fdbcc7-dcec-409f-8d1c-0eec6fa68f44'
  ) => {
    event.stopPropagation();

    const firstTeam = remainingTeams[0];
    if (!firstTeam || !firstTeam.channels?.length) return;

    const firstChannel = firstTeam.channels[0];
    if (!firstChannel) return;

    const encodedChannelName = encodeURIComponent(firstChannel.displayName);
    const url = `https://teams.microsoft.com/l/channel/${firstChannel.id}/${encodedChannelName}?groupId=${firstTeam.id}&tenantId=${tenantId}&app=web`;

    window.open(url, '_blank');
  };

  const fetchMeetings = async () => {
    try {
      const startOfDay = momentTimezone()
        .tz(timeZone)
        .startOf('day')
        .toISOString();
      const endOfDay = momentTimezone().tz(timeZone).endOf('day').toISOString();
      const res = await axios.get(
        `https://graph.microsoft.com/v1.0/me/calendarView?startDateTime=${startOfDay}&endDateTime=${endOfDay}`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );
      setExtData((prev) => ({
        ...prev,
        teammeeting: res.data.value || [],
      }));
    } catch (err) {
      console.error('Error fetching meetings:', err);
    }
  };

  const onRespond = async (event, eventId, responseType) => {
    event.stopPropagation();

    const url = `https://graph.microsoft.com/v1.0/me/events/${eventId}/${responseType}`;
    try {
      await axios.post(
        url,
        {
          comment: `Auto ${responseType}`,
          sendResponse: true,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        }
      );
      fetchMeetings(); // refresh list
    } catch (error) {
      console.error(`Error during ${responseType}:`, error);
    }
  };

  useEffect(() => {
    if (widget_name === 'ext_app_teams_meetings') {
      fetchMeetings();
    }
  }, [widget_name, accessToken]);

  return (
    <div className="mainCardContainer">
      <Paper className="cardContainer" style={{ borderRadius: '13px' }}>
        <div
          onClick={() => handleAction(widget_on_click_method)}
          style={{ pointerEvents: 'auto', cursor: 'pointer' }}
        >
          <div
            style={{
              content: '""',
              position: 'absolute',
              inset: 0,
              padding: '4px',
              borderRadius: '13px',
              background: 'linear-gradient(45deg, #9b59b6, #3498db, #2ecc71)', // Purple-Blue-Green Gradient
              WebkitMask:
                'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
              WebkitMaskComposite: 'xor',
              maskComposite: 'exclude',
              pointerEvents: 'auto',
            }}
          ></div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {widget_icon && (
              <div
                dangerouslySetInnerHTML={{ __html: widget_icon }}
                // style={{ width: '36px', height: '36px' }}
              />
            )}
            <div
              className="caredName"
              style={{
                fontSize: '1.2rem',
                fontWeight: 'bold',
                color: '#000000',
              }}
            >
              {widget_title || 'Title'}
            </div>
          </div>
          {/* <div className="cardValue">{widget_desc || 'Description'}</div> */}
          {summaryEntriesList.length > 0 && (
            <div
              style={{
                borderRadius: '6px',
                marginTop: '10px',
                padding: '6px',
                // maxHeight: '80px',
                // overflowY: 'auto',
                fontSize: '1rem',
              }}
            >
              {summaryEntriesList.map((entries, groupIndex) => (
                <div key={groupIndex} style={{ marginBottom: '12px' }}>
                  {entries.map((entry, entryIndex) => (
                    <div key={entryIndex} style={{ marginBottom: '4px' }}>
                      <div style={{ fontWeight: 600 }}>{entry.label}</div>
                      <div>
                        <span style={{ fontWeight: 800 }}>{entry.value}</span>
                        {entry.unit && <span> {entry.unit}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
          {/* <div className="circleOne"></div>
          <div className="circleTwo"></div> */}

          {widget_name === 'ext_app_teams' && extData?.teams?.length > 0 && (
            <div>
              <h3>Teams Channels</h3>
              <ul>
                {extData?.teams?.slice(0, 3)?.map((team) => {
                  const firstTwo = team?.channels.slice(0, 2);
                  const restChannels = team?.channels.slice(2);

                  return (
                    <li key={team.id}>
                      <strong>{team.displayName}</strong>
                      <ul style={{ paddingLeft: '1rem' }}>
                        {firstTwo.map((channel) => (
                          <li key={channel.id}>{channel.displayName}</li>
                        ))}
                        {restChannels.length > 0 && (
                          <li>
                            <span
                              style={{
                                cursor: 'pointer',
                                color: '#0078d4',
                                textDecoration: 'underline',
                                zIndex: 1000,
                                position: 'relative',
                              }}
                              onClick={(e) =>
                                handleOpenMore(
                                  e,
                                  team.id,
                                  restChannels[0]?.id,
                                  restChannels[0]?.displayName
                                )
                              }
                            >
                              +{restChannels.length} more
                            </span>
                          </li>
                        )}
                      </ul>
                    </li>
                  );
                })}
                {extData.teams.length > 3 && (
                  <li>
                    <span
                      style={{
                        cursor: 'pointer',
                        color: '#0078d4',
                        textDecoration: 'underline',
                        zIndex: 1000,
                        position: 'relative',
                      }}
                      onClick={(e) =>
                        handleOpenMoreTeams(e, extData.teams.slice(3))
                      }
                    >
                      +{extData.teams.length - 3} more teams
                    </span>
                  </li>
                )}
              </ul>
            </div>
          )}
          {widget_name === 'ext_app_teams_meetings' &&
            extData?.teammeeting?.length > 0 && (
              <MeetingList
                meetings={extData?.teammeeting?.filter(
                  (meeting) => !meeting.isCancelled
                )}
                onRespond={onRespond}
              />
            )}

          {widget_name === 'ext_app_jira' && extData?.jira && (
            <>
              <div>Latest Ticket Number: {extData?.jira?.issues?.[0]?.key}</div>
              <hr />
              <div>
                Ticket Title: {extData?.jira?.issues?.[0]?.fields?.summary}
              </div>
              <hr />
              <div>
                Ticket Status:{' '}
                {extData?.jira?.issues?.[0]?.fields?.status?.name}
              </div>
              <hr />
              <div>
                Ticket Reported By:{' '}
                {extData?.jira?.issues?.[0]?.fields?.reporter?.displayName}
              </div>
              <hr />
              <br />
              <div style={{ fontWeight: 600 }}>
                Total Assigned Tickets: {extData?.jira?.total}
              </div>
            </>
          )}
          {widget_name === 'ext_app_dynamics_crm' && crmaccessToken && (
            <CRMStatsDashboard accessToken={crmaccessToken} />
          )}
        </div>
        {/* <Box display="flex" justifyContent="flex-end" mt={2}> */}
        <Box display="flex" justifyContent="flex-end" mt="auto" pt={2}>
          {action_button2_caption && action_2_permission && (
            <Button
              sx={{
                bgcolor: '#444c68',
                color: '#fff',
                mr: 2,
                px: 2,
                textTransform: 'capitalize',
                '&:hover': {
                  transform: 'translateY(-1.36px)',
                  backgroundColor: 'rgb(38, 31, 62)',
                },
              }}
              onClick={() => handleAction(action_button2_method)}
              // variant="contained"
            >
              {action_button2_caption}
            </Button>
          )}
          {action_button1_caption && action_1_permission && (
            <Button
              variant="contained"
              onClick={() => handleAction(action_button1_method)}
              sx={{
                textTransform: 'capitalize',
                boxShadow: 'none',
                px: 2,
                '&:hover': {
                  transform: 'translateY(-1.36px)',
                },
              }}
            >
              {action_button1_caption}
            </Button>
          )}
        </Box>
      </Paper>
    </div>
  );
}
function addSearchParam(key, value) {
  const url = new URL(window.location.href);
  url.searchParams.set(key, value); // Adds or updates the parameter
  window.history.replaceState({}, '', url); // Updates the URL without reloading
}
